import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumb from "../../../components/layout/Breadcrumb";
import { fetchAnnouncementById, updateAnnouncement } from "../../../redux/slice/announcementsSlice";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { Editor } from "@tinymce/tinymce-react";
import { useHasPermission } from "../../../hooks/useHasPermission";

export default function AnnouncementEdit() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const editorRef = useRef(null);

  const { selected, loading } = useSelector(
    (state) => state.announcements
  );

  const canManage = useHasPermission("Announcements-manage");

  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("DRAFT");
  const [publishAt, setPublishAt] = useState("");
  const [contentHtml, setContentHtml] = useState("");

  useEffect(() => {
    if (id) {
      dispatch(fetchAnnouncementById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (selected?.id === Number(id)) {
      setTitle(selected.title || "");
      setStatus(selected.status || "DRAFT");
      setPublishAt(selected.publishAt?.slice(0, 16) || "");
      setContentHtml(selected.content || "");
    }
  }, [selected, id]);

  const handleUpdate = async (e) => {
    e.preventDefault();

    const editorContent = editorRef.current
      ? editorRef.current.getContent()
      : contentHtml;

    if (status === "SCHEDULED" && !publishAt) {
      toast.error("Publish date is required for scheduled announcements", { className: 'toast-error-custom' });
      return;
    }

    const payload = {
      title,
      content: editorContent,
      status,
      type: "ANNOUNCEMENT",
      ...(status === "SCHEDULED" && { publishAt }),
    };

    try {
      const res = await dispatch(updateAnnouncement({ id, payload })).unwrap();
      console.log(res);
      toast.success("Announcement updated successfully!", { className: "toast-success-custom", });
      navigate("/admin/announcements");
    } catch (err) {
      toast.error("Update failed: " + (err?.message || err));
    }
  };

  const tinyConfig = {
    height: 500,
    menubar: "file edit view insert format tools table image help",
    branding: false,
    promotion: false,
    statusbar: true,
    resize: true,

    plugins: [
      "advlist", "autolink", "lists", "link", "image", "charmap", "preview", "anchor",
      "searchreplace", "visualblocks", "code", "fullscreen", "insertdatetime",
      "media", "table", "help", "wordcount"
    ],

    toolbar:
      "undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | " +
      "image | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | " +
      "link media table | searchreplace preview fullscreen code | help",

    content_style:
      "body { font-family: Helvetica, Arial, sans-serif; font-size:14px }",
    image_title: false,
    image_dimensions: false,
    image_advtab: false,
    file_picker_types: "image",

    file_picker_callback: (callback, value, meta) => {
      if (meta.filetype !== "image") return;
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = () => {
        const file = input.files[0];
        const reader = new FileReader();
        reader.onload = () => {
          callback(reader.result, { alt: file.name });
        };
        reader.readAsDataURL(file);
      };
      input.click();
    },

    images_upload_handler: (blobInfo) =>
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blobInfo.blob());
      }),
  };

  if (!canManage) {
    return (
      <p className="perm-msg">
        You don’t have permission to edit announcements!
      </p>
    );
  }

  if (loading) return <p>Loading announcement...</p>;

  return (
    <div className="section-body announcement-update">
      <div className="container-fluid">
        <h5 className="mb-3 mt-1">Edit Announcement</h5>
        <div className="d-flex justify-content-between mt-3 mb-4 align-middle">
          <Breadcrumb
            overrideTrail={[
              { name: "Dashboard", path: "/admin/dashboard" },
              { name: "Announcements", path: "/admin/announcements" },
              { name: `Edit (${selected?.title || ""})` }, // no path → active
            ]}
          />
          <div className="d-flex align-items-end">
            <div className="policy-status-toggle">
              <input type="radio" id="published" name="announcementStatus" checked={status === "PUBLISHED"} onChange={() => setStatus("PUBLISHED")} />
              <label htmlFor="published" className="publish m-0">
                Publish
              </label>

              <input type="radio" id="draft" name="announcementStatus" checked={status === "DRAFT"} onChange={() => setStatus("DRAFT")} />
              <label htmlFor="draft" className="draft m-0">
                Draft
              </label>

              <input type="radio" id="scheduled" name="announcementStatus" checked={status === "SCHEDULED"} onChange={() => setStatus("SCHEDULED")} />
              <label htmlFor="scheduled" className="scheduled m-0">
                Scheduled
              </label>
            </div>

            <Button type="submit" form="announcementForm" className="primary-btn ml-4" >
              Update Announcement
            </Button>
          </div>
        </div>

        <div className="card mb-5">
          <div className="card-header">
            <Form id="announcementForm" onSubmit={handleUpdate}>
              <Form.Group className="mb-3">
                <Form.Label><strong>Announcement Title:</strong></Form.Label>
                <Form.Control
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </Form.Group>

              {status === "SCHEDULED" && (
                <Form.Group className="mb-3">
                  <Form.Label><strong>Publish Date & Time:</strong></Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={publishAt}
                    onChange={(e) => setPublishAt(e.target.value)}
                    required
                  />
                </Form.Group>
              )}

              <Form.Group className="mb-4">
                <Form.Label><strong>Announcement Content:</strong></Form.Label>
                <Editor
                  onInit={(evt, editor) => (editorRef.current = editor)}
                  initialValue={contentHtml}
                  init={tinyConfig}
                  apiKey="x1zlwv4dd93x8wuuh7iovblba5p75zd4iwk50u953zh33s2p"
                />
              </Form.Group>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}
